import { useEffect, useState } from "react";
import {
  Grid,
  Typography,
  FormControl,
  FormControlLabel,
  TextField,
  Box,
  Button,
  IconButton,
  Tooltip,
  FormGroup,
  Switch,
  AlertColor,
  MenuItem,
  Select,
  CircularProgress,
} from "@mui/material";
import { FetchedData } from "./ContractorFormPage";
import { useNavigate, useParams } from "react-router-dom";
import { getJwtTokenFromIdToken } from "../auth/AuthFanc";
import IconButtonWithTooltip from "../../atoms/Button/IconButtonWithTooltip";
import LockResetRoundedIcon from "@mui/icons-material/LockResetRounded";
import WarningIcon from "@mui/icons-material/Warning";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import {
  GridColDef,
  GridColumnVisibilityModel,
  GridRenderCellParams,
} from "@mui/x-data-grid";
import {
  StyledDataGrid,
  customDataGridLocaleTextAll,
  customDataGridLocaleTextLoading,
  customDataGridLocaleTextNoData,
} from "../../../styles/styles";
import ConfirmDialog from "../../molecules/ConfirmDialog";
import React from "react";

type MasterKeyTableProps = {
  buildingData: FetchedData["building"];
  keysData: FetchedData["keys"];
  canSetKeyExpiration: boolean;
  isEnabled: boolean | undefined;
  setSnackbarOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setAlertSeverity: React.Dispatch<React.SetStateAction<AlertColor>>;
  setSnackbarMessage: React.Dispatch<React.SetStateAction<string>>;
};

export type MasterKey = {
  autoKeyId: number;
  keyIndexInContract: number;
  doorId: string;
  issuedAt: Date | undefined;
  activatedAt: Date | undefined;
  activatedById: string;
  password: string;
  phoneToken: string | undefined;
  lastUsedAt: Date | undefined;
  isEnabled: boolean;
  isKeyGenerated: boolean;
  isFeliCaManaged: boolean;
  memo: string;
  expiredAt: Date | string | null;
  selectedExpirationMinutes: string | number | null;
  isEnabledModified?: boolean; // isEnabledの変更を追跡
  originalIsEnabled: boolean; // 元のisEnabled値を保持
};

// 有効期限の選択肢（分単位）
const expirationOptions = [
  { label: "変更しない", value: null },
  { label: "2分", value: 2 },
  { label: "60分", value: 60 },
  { label: "120分", value: 120 },
  { label: "6時間", value: 360 },
  { label: "24時間", value: 1440 },
];

// 日付をフォーマットする関数
const formatDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = ("0" + (date.getMonth() + 1)).slice(-2);
  const day = ("0" + date.getDate()).slice(-2);
  const hours = ("0" + date.getHours()).slice(-2);
  const minutes = ("0" + date.getMinutes()).slice(-2);
  return `${year}/${month}/${day} ${hours}:${minutes}`;
};

// 現在時刻に分数を加算して有効期限を設定し、秒数は0にリセット
const calculateExpirationDate = (minutesToAdd: number | null): Date | null => {
  if (minutesToAdd === null || minutesToAdd === undefined) return null;
  const now = new Date();
  now.setMinutes(now.getMinutes() + minutesToAdd);
  now.setSeconds(0, 0); // 秒とミリ秒を0にリセット
  return now;
};

const MasterKeyTable = ({
  buildingData,
  keysData,
  isEnabled,
  canSetKeyExpiration,
  setSnackbarOpen,
  setAlertSeverity,
  setSnackbarMessage,
}: MasterKeyTableProps) => {
  const [selectedSdcUuid, setSelectedSdcUuid] = useState<string>(""); // SDC選択用
  const [numberOfKeys, setNumberOfKeys] = useState<number>(1); // 鍵の数
  const [unissuedSdcs, setUnissuedSdcs] = useState<string[]>([]); // 鍵未発行のドア

  const { autoBuildingId, autoContractId } = useParams(); //URLパラメータから取得

  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true); // 初期状態を読み込み中に設定

  const [isUpdating, setIsUpdating] = useState(false);
  const [isGeneratingKey, setIsGeneratingKey] = useState(false);

  const [globalExpiration, setGlobalExpiration] = useState<number | null>(null);

  // 列の可視性を管理するためのステートを追加
  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({
      activatedById: false, // デフォルトで非表示
      password: false, // デフォルトで非表示
    });

  // 確認ダイアログ
  const [openConfirm, setOpenConfirm] = React.useState(false);
  const [openConfirmAddKey, setOpenConfirmAddKey] = useState(false);
  const [currentAutoKeyId, setCurrentAutoKeyId] = React.useState<
    number | undefined
  >(undefined);
  const handleUpdatePassword = (autoKeyId: number) => {
    setOpenConfirm(true);
    // 現在のautoKeyIdをstateに保存しておく
    setCurrentAutoKeyId(autoKeyId);
  };

  const confirmUpdatePassword = () => {
    if (currentAutoKeyId !== undefined) {
      updatePassword(currentAutoKeyId);
    }
    setOpenConfirm(false);
  };

  console.log("keysData:", keysData);

  const [masterKeys, setMasterKeys] = useState<MasterKey[]>([]);

  useEffect(() => {
    setMasterKeys(
      keysData.map((key) => ({
        autoKeyId: key.autoKeyId,
        keyIndexInContract: key.keyIndexInContract,
        doorId: key.sdc?.doorPlace || "",
        issuedAt: new Date(key.createdAt),
        activatedAt: key.activatedAt ? new Date(key.activatedAt) : undefined,
        activatedById: key.activateId,
        password: key.password,
        phoneToken: key.authToken,
        lastUsedAt: key.lastUsedAt ? new Date(key.lastUsedAt) : undefined,
        isEnabled: key.isEnabled,
        isKeyGenerated: key.canCreateDuplicateKey,
        isFeliCaManaged: key.canManageFelica,
        memo: key.memo,
        sdc: key.sdc, // sdcを渡す
        expiredAt: key.expiredAt ? new Date(key.expiredAt) : null,
        selectedExpirationMinutes: key.expiredAt
          ? expirationOptions.find((option) => {
              const expirationDate = calculateExpirationDate(
                typeof option.value === "string" ? null : option.value
              );
              return (
                key.expiredAt &&
                new Date(key.expiredAt).getTime() === expirationDate?.getTime()
              );
            })?.value || null
          : null,
        originalIsEnabled: key.isEnabled, // 元のisEnabled値を保存
      }))
    );
  }, [keysData]);

  // 未発行のドアを特定
  useEffect(() => {
    // keysData の sdc.autoSdcId を取得して Set に格納
    const issuedSdcIds = new Set(
      keysData
        .filter((key) => key.sdc) // sdc が存在するデータのみ対象
        .map((key) => key.sdc.autoSdcId)
    );

    // buildingData.sdcs から鍵が発行されていないドアを抽出
    const unissued = buildingData.sdcs
      .filter(
        (sdc) =>
          !issuedSdcIds.has(sdc.autoSdcId) && // 発行済みのIDに含まれていない
          sdc.isAalEnabled // isAalEnabledがtrueである
      )
      .map((sdc) => sdc.doorPlace); // ドア名を取得

    setUnissuedSdcs(unissued); // 状態を更新
  }, [buildingData, keysData]);

  // スマホキー情報保存が押された時に、expiredAtが未設定の場合にデフォルトの有効期限をセットし、APIに送信
  const handleUpdateAll = async () => {
    const updatedKeys = masterKeys.map((key) => {
      const updatedExpiredAt = !key.isEnabled
        ? new Date() // 無効化された場合は現在時刻を設定
        : key.selectedExpirationMinutes !== null
        ? calculateExpirationDate(
            typeof key.selectedExpirationMinutes === "string"
              ? null
              : key.selectedExpirationMinutes
          )
        : key.expiredAt; // selectedExpirationMinutesを基に有効期限を設定

      return {
        ...key,
        expiredAt: updatedExpiredAt,
        selectedExpirationMinutes: null, // 成功後にセレクターをリセット
        isEnabledModified: false, // 更新成功後にフラグをリセット
        originalIsEnabled: key.isEnabled, // 新しい状態を元の状態として保存
      };
    });

    setMasterKeys(updatedKeys);

    try {
      await updateMasterKey(updatedKeys);
      setSnackbarMessage("更新が完了しました。");
      setAlertSeverity("success");
      setSnackbarOpen(true);

      // グローバルセレクターも「変更しない」にリセット
      setGlobalExpiration(null);
    } catch (error: unknown) {
      setSnackbarMessage("更新に失敗しました。");
      setAlertSeverity("error");
      setSnackbarOpen(true);
      console.error("更新エラー: ", error);
    }
  };

  // 行の変更を管理し、変更されたフラグを設定
  const handleMasterKeyChange = (id: number, key: string, value: any) => {
    setMasterKeys((prevMasterKeys) =>
      prevMasterKeys.map((masterKey) =>
        masterKey.autoKeyId === id
          ? {
              ...masterKey,
              [key]: value, // selectedExpirationMinutesを更新するが、expiredAtは保存時に更新
              isEnabledModified:
                key === "isEnabled"
                  ? value !== masterKey.originalIsEnabled
                  : masterKey.isEnabledModified, // isEnabledの変更を追跡
            }
          : masterKey
      )
    );
  };

  // 全ての行の有効期限を上書き
  const handleGlobalExpirationChange = (newExpiration: number | null) => {
    setGlobalExpiration(newExpiration);
    setMasterKeys((prevMasterKeys) =>
      prevMasterKeys.map((masterKey) => ({
        ...masterKey,
        selectedExpirationMinutes: newExpiration,
      }))
    );
  };

  // APIにマスターキーの情報を送信
  const updateMasterKey = async (updatedKeys: MasterKey[]) => {
    const jwtToken = await getJwtTokenFromIdToken();

    try {
      const apiUrl = canSetKeyExpiration
        ? `/contracts/${autoContractId}/update-keys-as-time-limited`
        : `/contracts/${autoContractId}/keys`;

      const requestBody = JSON.stringify({
        keys: updatedKeys.map((key) => ({
          autoKeyId: key.autoKeyId,
          isEnabled: key.isEnabled,
          canCreateDuplicateKey: key.isKeyGenerated,
          canManageFelica: key.isFeliCaManaged,
          memo: key.memo,
          ...(canSetKeyExpiration && {
            expiredAt: key.selectedExpirationMinutes
              ? calculateExpirationDate(
                  typeof key.selectedExpirationMinutes === "string"
                    ? null
                    : key.selectedExpirationMinutes
                )
              : key.expiredAt,
          }),
        })),
      });

      const response = await fetch(
        process.env.REACT_APP_API_ENDPOINT + apiUrl,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: jwtToken,
          },
          body: requestBody,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }
    } catch (error: unknown) {
      throw error;
    }
  };

  const updatePassword = async (autoKeyId: number) => {
    // JWTトークンを取得します
    const jwtToken = await getJwtTokenFromIdToken();
    try {
      // call updateKeyPassword API
      const response = await fetch(
        process.env.REACT_APP_API_ENDPOINT +
          `/keys/${autoKeyId}/update-password`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: jwtToken,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      const responseData = await response.json();
      console.log("レスポンスデータ：" + JSON.stringify(responseData));

      setMasterKeys((prevMasterKeys) =>
        prevMasterKeys.map((masterKey) =>
          masterKey.autoKeyId === autoKeyId
            ? { ...masterKey, password: responseData.password }
            : masterKey
        )
      );
      setSnackbarMessage("パスワードが更新されました。");
      setAlertSeverity("success");
      setSnackbarOpen(true);
    } catch (error: unknown) {
      if (error instanceof Error) {
        console.error("Error updating password:", error);
        setSnackbarMessage(
          "パスワードの更新に失敗しました。エラー：" + error.message
        );
        setAlertSeverity("error");
        setSnackbarOpen(true);
      } else {
        console.error("Unknown error caught:", error);
        setSnackbarMessage(
          "パスワードの更新に失敗しました。不明なエラーが発生しました。"
        );
        setAlertSeverity("error");
        setSnackbarOpen(true);
      }
    }
  };

  const handleTransitionButtonClick = () => {
    console.log("QR表示");
    navigate(
      `/buildings/${autoBuildingId}/contractors/${autoContractId}/key-qr/all`
    );
  };
  const selectedDoorPlace =
    buildingData.sdcs.find((sdc) => sdc.sdcUuid === selectedSdcUuid)
      ?.doorPlace || "選択されていません";

  const confirmAddKeys = async () => {
    if (selectedSdcUuid && numberOfKeys > 0) {
      await handleAddKeysButtonClick(selectedSdcUuid, numberOfKeys);
    }
    setOpenConfirmAddKey(false); // ダイアログを閉じる
  };

  const handleAddKeysButtonClick = async (
    sdcUuid: string,
    numberOfKeys: number
  ) => {
    console.log(`鍵追加発行 - SDC: ${sdcUuid}, 鍵数: ${numberOfKeys}`);

    const jwtToken = await getJwtTokenFromIdToken();
    try {
      const response = await fetch(
        process.env.REACT_APP_API_ENDPOINT +
          `/contracts/${autoContractId}/keys-by-sdc`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: jwtToken,
          },
          body: JSON.stringify({
            sdcUuid,
            numberOfKeys,
          }),
        }
      );

      if (!response.ok) {
        console.log("response.status:" + response.status);
        const errorData = await response.json();
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      const responseData = await response.json();
      console.log("レスポンスデータ：" + JSON.stringify(responseData));

      // 発行された鍵のドア名を取得
      const doorPlace =
        buildingData.sdcs.find((sdc) => sdc.sdcUuid === sdcUuid)?.doorPlace ||
        "不明なドア";

      // masterKeys を更新
      setMasterKeys((prevKeys) => [
        ...prevKeys,
        ...responseData["keys"].map((key: any) => ({
          autoKeyId: key.autoKeyId,
          keyIndexInContract: key.keyIndexInContract,
          doorId: doorPlace, // ここでドア名を設定
          issuedAt: new Date(key.createdAt),
          activatedAt: key.activatedAt ? new Date(key.activatedAt) : undefined,
          activatedById: key.activateId,
          password: key.password,
          phoneToken: key.authToken,
          lastUsedAt: key.lastUsedAt ? new Date(key.lastUsedAt) : undefined,
          isEnabled: key.isEnabled,
          isKeyGenerated: key.canCreateDuplicateKey,
          isFeliCaManaged: key.canManageFelica,
          memo: key.memo,
          expiredAt: key.expiredAt ? new Date(key.expiredAt) : null,
          selectedExpirationMinutes: null,
          originalIsEnabled: key.isEnabled,
        })),
      ]);

      // unissuedSdcs から該当ドアを削除
      setUnissuedSdcs((prevUnissuedSdcs) =>
        prevUnissuedSdcs.filter((door) => door !== doorPlace)
      );

      setSnackbarMessage("鍵が発行されました。");
      setAlertSeverity("success");
      setSnackbarOpen(true);
    } catch (error: unknown) {
      if (error instanceof Error) {
        console.error("鍵発行エラー:", error);
        setSnackbarMessage("鍵発行に失敗しました。エラー：" + error.message);
      } else {
        console.error("不明なエラー:", error);
        setSnackbarMessage(
          "鍵発行に失敗しました。不明なエラーが発生しました。"
        );
      }
      setAlertSeverity("error");
      setSnackbarOpen(true);
    }
  };

  // masterKeysの初期値が設定されるときに読み込みを終了とみなす
  useEffect(() => {
    setIsLoading(false);
  }, [masterKeys]);

  const columns: GridColDef[] = [
    {
      field: "keyIndexInContract",
      headerName: "鍵番号",
      flex: 0.5,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "doorId",
      headerName: "対象ドア",
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <Typography sx={{ fontSize: "0.8rem" }}>
            <span>{params.value}</span>
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: "issuedAt",
      headerName: "発行日時",
      flex: 1,
      renderCell: (params) => {
        const formattedDate = params.value
          ? new Date(params.value).toLocaleString()
          : "";
        return (
          <Tooltip title={formattedDate}>
            <span>{formattedDate}</span>
          </Tooltip>
        );
      },
    },
    {
      field: "activatedAt",
      headerName: "アクティベート日時",
      flex: 1,
      renderCell: (params) => {
        const formattedDate = params.value
          ? new Date(params.value).toLocaleString()
          : "";
        return (
          <Tooltip title={formattedDate}>
            <span>{formattedDate}</span>
          </Tooltip>
        );
      },
    },
    {
      field: "activatedById",
      headerName: "アクティベートID",
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "password",
      headerName: "パスワード",
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "lastUsedAt",
      headerName: "最終使用日時",
      flex: 1,
      renderCell: (params) => {
        const formattedDate = params.value
          ? new Date(params.value).toLocaleString()
          : "";
        return (
          <Tooltip title={formattedDate}>
            <span>{formattedDate}</span>
          </Tooltip>
        );
      },
    },
    {
      field: "isEnabled",
      headerName: "有効・無効",
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        const isContractDisabled = !isEnabled; // 契約情報が無効
        const isSdcAalEnabled = params.row.sdc?.isAalEnabled ?? true; // SDCのisAalEnabledを確認
        const isRowDisabled = isContractDisabled || !isSdcAalEnabled; // 行全体が無効かどうか
        const isNotSaved = params.row.isEnabledModified;

        return (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <FormControl component="fieldset">
              <FormGroup>
                <FormControlLabel
                  control={
                    isRowDisabled ? (
                      <Switch disabled checked={params.value} />
                    ) : (
                      <Switch
                        checked={params.value}
                        onChange={(e) => {
                          handleMasterKeyChange(
                            params.row.autoKeyId,
                            "isEnabled",
                            e.target.checked
                          );
                        }}
                        sx={{ transform: "scale(0.8)" }}
                      />
                    )
                  }
                  label={
                    <Typography sx={{ fontSize: "0.8rem" }}>
                      {params.value ? "有効" : "無効"}
                    </Typography>
                  }
                />
              </FormGroup>
            </FormControl>
            {/* 未反映の赤文字を表示 */}
            {isNotSaved && (
              <Typography
                sx={{ fontSize: "0.8rem", color: "red", marginLeft: 1 }}
              >
                未反映
              </Typography>
            )}
          </Box>
        );
      },
    },

    ...(canSetKeyExpiration
      ? [
          {
            field: "expiredAt",
            headerName: "有効期限",
            flex: 2,
            renderCell: (params: GridRenderCellParams) => {
              const now = new Date();
              const expiredAt = params.value ? new Date(params.value) : null;
              const isExpired = expiredAt && expiredAt < now;

              // 未反映状態のチェック：selectedExpirationMinutesが変更されたが、まだ保存されていない場合
              const isNotSaved =
                params.row.selectedExpirationMinutes !== null &&
                calculateExpirationDate(
                  params.row.selectedExpirationMinutes
                ) !== expiredAt;

              return (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  {/* 左に「有効中」または「期限切れ」を表示 */}
                  {params.row.isEnabled && (
                    <Typography
                      sx={{
                        fontSize: "0.8rem",
                        marginRight: 1,
                        color: isExpired ? "gray" : "red", // 有効期限が過去ならgray、未来なら赤
                      }}
                    >
                      {isExpired ? "期限切れ" : "有効中"}
                    </Typography>
                  )}
                  {/* 有効期限の日時を表示 */}
                  {params.row.isEnabled && (
                    <Typography
                      sx={{
                        fontSize: "0.8rem",
                        marginRight: 1,
                        color: isExpired ? "gray" : "red", // 有効期限が過去ならgray、未来なら赤
                      }}
                    >
                      {expiredAt ? formatDate(expiredAt) : "期限未設定"}
                    </Typography>
                  )}
                  {/* セレクター */}
                  <Select
                    size="small"
                    sx={{
                      fontSize: "0.8rem",
                      // 未反映の場合は赤枠
                      "& .MuiOutlinedInput-root": {
                        borderColor: isNotSaved ? "red" : "inherit",
                        borderWidth: "0.5px", // 枠線の太さを調整
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: isNotSaved ? "red" : "inherit",
                        borderWidth: isNotSaved ? "0.5px" : "1px", // 常に赤い枠線
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: isNotSaved ? "red" : "inherit",
                        borderWidth: isNotSaved ? "0.5px" : "1px", // hover時も同じ太さ
                      },
                    }}
                    value={
                      params.row.selectedExpirationMinutes === null
                        ? "no-change"
                        : params.row.selectedExpirationMinutes
                    }
                    onChange={(e) => {
                      const value =
                        e.target.value === "no-change"
                          ? null
                          : Number(e.target.value);
                      handleMasterKeyChange(
                        params.row.autoKeyId,
                        "selectedExpirationMinutes",
                        value
                      );
                    }}
                    renderValue={(selected) => {
                      if (selected === "no-change") return "変更しない";
                      const option = expirationOptions.find(
                        (opt) => opt.value === selected
                      );
                      return option ? option.label : "";
                    }}
                  >
                    {expirationOptions.map((option) => (
                      <MenuItem
                        key={option.value === null ? "no-change" : option.value}
                        value={
                          option.value === null ? "no-change" : option.value
                        }
                      >
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {/* 未反映の赤文字を表示 */}
                  {isNotSaved && (
                    <Typography
                      sx={{ fontSize: "0.8rem", color: "red", marginLeft: 1 }}
                    >
                      未反映
                    </Typography>
                  )}
                </Box>
              );
            },
          },
        ]
      : []),

    {
      field: "memo",
      headerName: "メモ",
      flex: 1,
      renderCell: (params: GridRenderCellParams) =>
        isEnabled ? (
          <TextField
            size="small"
            defaultValue={params.value}
            onBlur={(e) => {
              const newValue = e.target.value;
              handleMasterKeyChange(params.row.autoKeyId, "memo", newValue);
            }}
            onKeyDown={(e) => {
              e.stopPropagation(); // キーイベントの伝播を停止
            }}
            InputProps={{
              style: { fontSize: "0.8rem" },
            }}
          />
        ) : (
          <TextField
            disabled
            size="small"
            defaultValue={params.value}
            InputProps={{
              style: { fontSize: "0.8rem" },
            }}
          />
        ),
    },

    {
      field: "actions",
      headerName: "操作",
      flex: 1,
      renderCell: (params: GridRenderCellParams) => (
        <div>
          <IconButtonWithTooltip
            title="QRコード表示"
            onClick={() => {
              console.log("Show QR code for key", params.row.autoKeyId);

              navigate(
                `/buildings/${autoBuildingId}/contractors/${autoContractId}/key-qr/${params.row.autoKeyId}`
              );
            }}
            icon={<QrCode2Icon />}
          />
          {isEnabled ? (
            <IconButtonWithTooltip
              title="QRコード再発行"
              onClick={() => {
                console.log("Update Password for key", params.row.autoKeyId);
                handleUpdatePassword(params.row.autoKeyId);
                setOpenConfirm(true);
              }}
              icon={<LockResetRoundedIcon />}
            />
          ) : (
            <IconButton disabled>{<LockResetRoundedIcon />}</IconButton>
          )}
        </div>
      ),
    },
  ];

  return (
    <Grid container spacing={2}>
      {/* 鍵未発行のドアの表示 */}
      {unissuedSdcs.length > 0 && (
        <Grid item xs={12} display="flex" alignItems="center">
          <WarningIcon sx={{ color: "red", marginRight: 1 }} />
          <Typography variant="h6" sx={{ color: "red" }}>
            鍵が発行されていないドアがあります: {unissuedSdcs.join(", ")}
          </Typography>
        </Grid>
      )}
      <Grid
        item
        xs={12}
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {/* 発行済み鍵一覧は左寄せ */}
        <Typography variant="h6" style={{ marginRight: "auto" }}>
          発行済み鍵一覧
        </Typography>

        {/* 右側のコンテンツを右寄せ */}
        <Grid
          container
          alignItems="center"
          justifyContent="flex-end"
          sx={{ marginTop: 2, width: "auto" }}
        >
          {canSetKeyExpiration && (
            <Box display="flex" alignItems="center" sx={{ marginRight: 2 }}>
              <Typography variant="body1" sx={{ marginRight: 2 }}>
                有効期限一括操作
              </Typography>
              <FormControl sx={{ width: "200px" }}>
                <Select
                  size="small"
                  value={
                    globalExpiration === null ? "no-change" : globalExpiration
                  }
                  onChange={(e) => {
                    const value =
                      e.target.value === "no-change"
                        ? null
                        : Number(e.target.value);
                    handleGlobalExpirationChange(value);
                  }}
                  renderValue={(selected) => {
                    if (selected === "no-change") return "変更しない";
                    const option = expirationOptions.find(
                      (opt) => opt.value === selected
                    );
                    return option ? option.label : "";
                  }}
                >
                  {expirationOptions.map((option) => (
                    <MenuItem
                      key={option.value === null ? "no-change" : option.value}
                      value={option.value === null ? "no-change" : option.value}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          <Box display="flex" alignItems="center">
            <Typography variant="body1">鍵の発行設定</Typography>
            <FormControl>
              <Select
                value={selectedSdcUuid}
                onChange={(e) => setSelectedSdcUuid(e.target.value)}
                displayEmpty
                inputProps={{ "aria-label": "Select SDC" }}
                size="small"
                sx={{ marginLeft: 1 }}
              >
                <MenuItem value="" disabled>
                  ドアを選択
                </MenuItem>
                {buildingData.sdcs.map((sdc) => (
                  <MenuItem key={sdc.sdcUuid} value={sdc.sdcUuid}>
                    {sdc.doorPlace} (SDCSN: {sdc.sdcSerialNumber})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="発行する鍵の数"
              type="number"
              value={numberOfKeys}
              onChange={(e) => setNumberOfKeys(Number(e.target.value))}
              InputProps={{
                inputProps: { min: 1 }, // 1以上に制限
              }}
              size="small"
              sx={{ marginLeft: 1 }}
            />
            <Button
              variant="outlined"
              color="primary"
              onClick={async () => {
                setIsGeneratingKey(true);
                await confirmAddKeys();
                setIsGeneratingKey(false);
              }}
              disabled={
                !selectedSdcUuid || numberOfKeys <= 0 || isGeneratingKey
              }
              sx={{ marginLeft: 1 }}
            >
              {isGeneratingKey ? (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <CircularProgress size={24} sx={{ mr: 1 }} />
                  発行中...
                </Box>
              ) : (
                "鍵を発行"
              )}
            </Button>

            <Button
              variant="outlined"
              color="primary"
              onClick={() => handleTransitionButtonClick()}
              sx={{ marginLeft: 2 }}
            >
              全ての鍵のQRコード表示
            </Button>
          </Box>

          <ConfirmDialog
            open={openConfirmAddKey}
            onClose={() => setOpenConfirmAddKey(false)}
            onConfirm={confirmAddKeys}
            title="鍵の発行確認"
            description={`選択されたドア (${selectedDoorPlace}) に ${numberOfKeys} 個の鍵を発行します。よろしいですか？`}
          />
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <StyledDataGrid
          sx={{
            border: 0,
            "& .MuiDataGrid-row": {
              backgroundColor: (theme: { palette: { mode: string } }) =>
                theme.palette.mode === "light" ? "#fff" : "#303030",
              "&.Mui-disabled": {
                backgroundColor: "#e0e0e0", // グレーアウトした行の背景色
                pointerEvents: "none", // マウス操作を無効化
              },
            },
          }}
          rows={masterKeys}
          columns={columns}
          getRowClassName={(params) => {
            const isContractDisabled = !isEnabled; // 契約情報が無効
            const isSdcDisabled =
              params.row.sdc && params.row.sdc.isAalEnabled === false; // SDCが無効
            return isContractDisabled || isSdcDisabled ? "Mui-disabled" : "";
          }}
          autoHeight
          localeText={
            isLoading
              ? customDataGridLocaleTextLoading
              : masterKeys.length === 0
              ? customDataGridLocaleTextNoData
              : customDataGridLocaleTextAll
          }
          getRowId={(row) => row.autoKeyId}
          // 列の可視性モデルを指定
          columnVisibilityModel={columnVisibilityModel}
          // 列の可視性が変更されたときに状態を更新する
          onColumnVisibilityModelChange={(
            newModel: GridColumnVisibilityModel
          ) => {
            setColumnVisibilityModel(newModel);
          }}
        />
        <ConfirmDialog
          open={openConfirm}
          onClose={() => {
            setOpenConfirm(false);
          }}
          onConfirm={() => {
            confirmUpdatePassword();
          }}
          title="確認"
          description="現在のQRコードは利用不可になりますが、QRコードを再発行しますか？"
        />
      </Grid>

      <Grid item xs={12} marginTop={2}>
        <Box display="flex" justifyContent="flex-end">
          <Button
            disabled={!isEnabled || isUpdating}
            variant="outlined"
            color="primary"
            onClick={async () => {
              setIsUpdating(true);
              await handleUpdateAll();
              setIsUpdating(false);
            }}
            style={{ marginRight: "12px" }}
          >
            {isUpdating ? (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <CircularProgress size={24} sx={{ mr: 1 }} />
                更新中...
              </Box>
            ) : (
              "スマホキー情報保存"
            )}
          </Button>
        </Box>
      </Grid>
    </Grid>
  );
};

export default MasterKeyTable;
